import api from "app/api";
import useExcelUrl from "app/useExcelUrl";
import { globalMessageHandler } from "common";
import {
  AdvancedReportFilterType,
  AdvancedReportValueType,
} from "features/reports/AdvancedReport/useAdvancedReport";
import {
  AdvancedReportDataContext,
  AdvancedReportDataFilterType,
} from "features/reports/AdvancedReport/useAdvancedReportData";
import useSettings from "features/settings/useSettings";
import moment from "moment";
import { ReactNode, useEffect, useState } from "react";

interface CoordinatorReport3DataProviderProps {
  children?: ReactNode;
  visibleValues?: AdvancedReportValueType[];
  visibleFilters?: AdvancedReportFilterType[];
}

const FIXED_PARTNER_GROUP_IDS = [8];

export default function CoordinatorReport3DataProvider({
  children,
  visibleValues,
  visibleFilters,
}: CoordinatorReport3DataProviderProps) {
  const [filter, setFilter] = useState<AdvancedReportDataFilterType>({
    from: moment().startOf("week").format("YYYY-MM-DD"),
    to: moment().endOf("week").format("YYYY-MM-DD"),
    partnerGroupIds: FIXED_PARTNER_GROUP_IDS,
  });
  const [loading, setLoading] = useState(false);
  const [excelWithBooking, setExcelWithBooking] = useState(true);
  const [data, setData] = useState([]);
  const [settings] = useSettings({ key: "advancedReport" });

  async function load() {
    setLoading(true);
    const { success, data, error } = await api.get(
      "reports/advanced-coordinator",
      {
        params: {
          ...filter,
          partnerGroupIds: FIXED_PARTNER_GROUP_IDS,
          currency: settings.currency,
          dateType: settings.dateType,
        },
      },
    );
    setLoading(false);
    if (success) {
      setData(data);
    } else {
      globalMessageHandler.alert({
        severity: "error",
        message: typeof error === "string" ? error : JSON.stringify(error),
      });
    }
  }

  const excelUrl = useExcelUrl(
    "reports",
    null,
    {
      currency: settings.currency,
      ...settings,
      ...filter,
      partnerGroupIds: FIXED_PARTNER_GROUP_IDS,
      bookings: excelWithBooking ? 1 : 0,
    },
    false,
  );

  useEffect(() => {
    load();
  }, [
    filter.programIds,
    filter.boatIds,
    filter.from,
    filter.to,
    settings.currency,
    settings.dateType,
  ]);

  return (
    <AdvancedReportDataContext.Provider
      value={{
        data,
        filter,
        setFilter,
        load,
        loading,
        excelUrl,
        excelWithBooking,
        setExcelWithBooking,
        visibleValues,
        visibleFilters,
      }}
    >
      {children}
    </AdvancedReportDataContext.Provider>
  );
}
